type WebApp = NonNullable<NonNullable<Window["Telegram"]>["WebApp"]>;

// The SDK object lives for this page launch, across React StrictMode remounts.
const initialized = new WeakSet<WebApp>();
const requestedFullscreen = new WeakSet<WebApp>();
const expandedFallback = new WeakSet<WebApp>();

/** Compare numeric API components (7.10 is newer than 7.7). */
export function supportsTelegramVersion(app: WebApp, minimum: string) {
  try {
    if (app.isVersionAtLeast) return app.isVersionAtLeast(minimum);
  } catch { /* Some wrappers expose a checker before their bridge is ready. */ }
  if (!/^\d+(?:\.\d+)*$/.test(app.version || "")) return false;
  const actual = app.version!.split(".").map(Number);
  const required = minimum.split(".").map(Number);
  for (let index = 0; index < Math.max(actual.length, required.length); index++) {
    const difference = (actual[index] || 0) - (required[index] || 0);
    if (difference) return difference > 0;
  }
  return true;
}

/** Request fullscreen once per Telegram launch; return the listener cleanup. */
export function initializeTelegramWindow(app: WebApp | undefined) {
  if (!app?.initData) return () => {};

  const expand = () => {
    if (app.isFullscreen || expandedFallback.has(app)) return;
    expandedFallback.add(app);
    try { app.expand?.(); } catch { /* A native failure must not prevent opening the app. */ }
  };
  const onFailure = (event?: unknown) => {
    // ALREADY_FULLSCREEN can arrive before a wrapper refreshes isFullscreen.
    if (event && typeof event === "object" && "error" in event && event.error === "ALREADY_FULLSCREEN") return;
    if (requestedFullscreen.has(app)) expand();
  };
  // Telegram may emit fullscreenFailed synchronously during requestFullscreen.
  app.onEvent?.("fullscreenFailed", onFailure);
  if (!initialized.has(app)) {
    initialized.add(app);
    try { app.ready?.(); } catch { /* The web workspace remains usable. */ }
    if (supportsTelegramVersion(app, "7.7")) {
      try { app.disableVerticalSwipes?.(); } catch { /* Unsupported native gestures must not break fullscreen. */ }
    }
    if (!app.isFullscreen) {
      try {
        if (app.requestFullscreen && supportsTelegramVersion(app, "8.0")) {
          requestedFullscreen.add(app);
          app.requestFullscreen!();
        } else expand();
      } catch { expand(); }
    }
  }

  return () => app.offEvent?.("fullscreenFailed", onFailure);
}
